/**
 * Ф2: «План-факт по днях» і «Точність».
 *
 * Компонування те саме, що в «Плановому доході»: заголовок секції поза плиткою,
 * графік у своїй плитці, транспонована таблиця через спільний FinHourlyTable,
 * під нею рядок пояснень.
 *
 * 🔶 Світлофор ТЗ A3 (🟢 ≥ −5% · 🟡 −5…−20% · 🔴 < −20%) реалізовано ДВОМА
 * кольорами замість трьох: критична смуга — той самий бурштин, але зі значком
 * попередження. Дизайн-контракт кабінету тримає червоний виключно за реальним
 * збоєм, і недовиконання плану під це визначення не підпадає; три стани при
 * цьому лишаються різними на вигляд.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { useState } from 'react';
import {
  CalendarDays, Send, Sun, GitCompareArrows, Coins, Banknote, AlertTriangle,
  Target, Gauge, Split, TrendingUp, type LucideIcon,
} from 'lucide-react';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';
import FinHourlyTable from '@/pages/financial/result/FinHourlyTable';
import type { FinCol, FinRow } from '@/pages/financial/result/FinHourlyTable';
import type { PlanFactResp, AccuracyResp } from '../types';
import { mln, n0, n1, n2, pct } from '../types';

/** Колір за порогами ТЗ A3; критична смуга — бурштин + значок (див. шапку). */
const deltaColor = (d: number | null | undefined) => {
  if (d == null) return undefined;
  if (d >= -0.05) return 'var(--ok)';
  return 'var(--warn)';
};
const isCritical = (d: number | null | undefined) => d != null && d < -0.20;

const dd = (iso: string) => iso.slice(8, 10);

// ── План-факт по днях ───────────────────────────────────────────────────────
export function PlanFactSection({ data }: { data: PlanFactResp }) {
  const { t } = useTranslation();
  const ct = useChartTheme();
  const [activeKey, setActiveKey] = useState<string | number | null>(null);
  const c = data.mtd.comparable;

  const header = (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <CalendarDays size={17} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
        <h3 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>
          {t('ak_bud2_plan_vs_actual_by_day')}
        </h3>
      </div>
      <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>
        {t('ak_bud2_plan_accepted_mms_nomination_actual_gr')}
      </span>
    </div>
  );

  const rows = data.days;
  if (!rows.length) {
    return (
      <section>{header}
        <div className="tile p-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud2_no_data_for_this_month')}
        </div>
      </section>
    );
  }

  const chartData = rows.map((r) => ({
    d: dd(r.day),
    plan: r.planMmsKwh == null ? null : r.planMmsKwh / 1000,
    fact: r.factKwh == null ? null : r.factKwh / 1000,
    delta: r.deltaPct == null ? null : r.deltaPct * 100,
  }));

  const cols: FinCol[] = [
    ...rows.map((r) => ({ key: r.day, label: dd(r.day) })),
    { key: 'sum', label: t('ak_bud2_total'), strong: true },
  ];

  const tableRows: FinRow[] = [
    {
      name: t('ak_bud2_mms_plan_mwh'), Icon: Send, color: ct.series.s3,
      cells: [
        ...rows.map((r) => ({ v: r.planMmsKwh == null ? '—' : n1(r.planMmsKwh / 1000) })),
        { v: n1(c.planKwh / 1000) },
      ],
    },
    {
      name: t('ak_bud2_actual_export_mwh'), Icon: Sun, color: ct.series.ok,
      cells: [
        ...rows.map((r) => ({ v: r.factKwh == null ? '—' : n1(r.factKwh / 1000) })),
        { v: n1(data.mtd.factKwh / 1000) },
      ],
    },
    {
      name: t('ak_bud2_deviation'), Icon: GitCompareArrows, color: 'var(--text-muted)',
      cells: [
        ...rows.map((r) => ({
          v: r.deltaPct == null ? '—' : `${r.deltaPct > 0 ? '+' : ''}${n1(r.deltaPct * 100)}`,
          color: deltaColor(r.deltaPct),
        })),
        {
          v: c.deltaPct == null ? '—' : `${c.deltaPct > 0 ? '+' : ''}${n1(c.deltaPct * 100)}`,
          color: deltaColor(c.deltaPct),
        },
      ],
    },
    {
      name: t('ak_bud2_dam_price_actual_uah_mwh'), Icon: Coins, color: 'var(--text-muted)',
      cells: [
        ...rows.map((r) => ({ v: n0(r.priceFact) })),
        { v: n0(data.mtd.factKwh > 0 ? (data.mtd.factRevenue / (data.mtd.factKwh / 1000)) : null) },
      ],
    },
    {
      name: t('ak_bud2_actual_revenue_uah_k'), Icon: Banknote, strong: true,
      cells: [
        ...rows.map((r) => ({ v: r.factRevenue == null ? '—' : n0(r.factRevenue / 1000) })),
        { v: n0(data.mtd.factRevenue / 1000) },
      ],
    },
  ];

  const crit = rows.filter((r) => isCritical(r.deltaPct));

  return (
    <section>
      {header}

      <div className="tile p-4 mb-3">
        <div style={{ height: 258 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
              onMouseMove={(st: { activeLabel?: string | number }) => {
                const lbl = st?.activeLabel;
                setActiveKey(lbl == null ? null : (rows.find((r) => dd(r.day) === String(lbl))?.day ?? null));
              }}
              onMouseLeave={() => setActiveKey(null)}>
              <CartesianGrid {...ct.gridProps} />
              <XAxis dataKey="d" {...ct.axis} />
              <YAxis yAxisId="v" {...ct.axis} width={48} label={ct.axisLabel(t('ak_bud2_mwh_2'))} />
              <YAxis yAxisId="d" orientation="right" {...ct.axis} width={44}
                label={ct.axisLabel(t('ak_bud2_x'), 90)} />
              <Tooltip {...ct.tooltip} cursor={ct.cursorBar}
                formatter={(v: number | string, name: string) => {
                  const x = typeof v === 'number' ? v : Number(v);
                  return name === t('ak_bud2_deviation_2') ? [`${n1(x)}%`, name] : [t('ak_bud3_mwh', { v: n1(x) }), name];
                }} />
              <ReferenceLine yAxisId="d" y={0} stroke={ct.grid} />
              <Bar yAxisId="v" dataKey="plan" name={t('ak_bud2_mms_plan')}
                {...ct.bar} fill={ct.series.s3} stroke={ct.series.s3} />
              <Bar yAxisId="v" dataKey="fact" name={t('ak_bud2_actual_2')}
                {...ct.bar} fill={ct.series.ok} stroke={ct.series.ok} />
              <Line yAxisId="d" dataKey="delta" name={t('ak_bud2_deviation_2')}
                {...ct.lineThin} stroke={ct.series.s2} strokeDasharray="4 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend items={[
          { label: t('ak_bud2_mms_plan'), color: ct.series.s3, bar: true },
          { label: t('ak_bud2_actual_export'), color: ct.series.ok, bar: true },
          { label: t('ak_bud2_deviation'), color: ct.series.s2, dashed: true },
        ]} />
      </div>

      <FinHourlyTable firstLabel={t('ak_bud2_metric_day')} cols={cols} rows={tableRows}
        minWidth={1180} activeColKey={activeKey} cornerIcon={CalendarDays} />

      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-3 text-[11px]"
        style={{ color: 'var(--text-dim)' }}>
        <span>
          {t('ak_bud2_comparable_both_plan_and_actual')} <b style={{ color: 'var(--text-muted)' }}>{c.days}</b> {t('ak_bud2_days')} {t('ak_bud2_plan')} {n1(c.planKwh / 1000)} / {t('ak_bud2_actual')} {n1(c.factKwh / 1000)} {t('ak_bud2_mwh_2')}
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>
          {t('ak_bud2_total_of_actuals_across_all')} <b style={{ color: 'var(--text-muted)' }}>{data.mtd.daysWithFact}</b> {t('ak_bud2_days_with_actuals')}
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>{t('ak_bud2_spec_thresholds_5_5_20_below_20')}</span>
        {crit.length > 0 && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span className="inline-flex items-center gap-1" style={{ color: 'var(--warn)' }}>
              <AlertTriangle size={12} strokeWidth={2} />
              {t('ak_bud2_below_20_a_reason_from_the_dispatcher', { days: crit.map((r) => dd(r.day)).join(', ') })}
            </span>
          </>
        )}
      </div>
    </section>
  );
}

// ── Точність ───────────────────────────────────────────────────────────────
/** Плитка метрики з порівнянням до цілі ТЗ A4. */
function Metric({ label, value, target, ok, hint }: {
  label: string; value: string; target?: string; ok?: boolean | null; hint?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="py-2" style={{ borderTop: '1px solid var(--line)' }}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12px]" style={{ color: 'var(--text)' }}>{label}</span>
        <span className="font-display text-[14px] shrink-0"
          style={{ color: ok == null ? 'var(--text)' : ok ? 'var(--ok)' : 'var(--warn)' }}>
          {value}
        </span>
      </div>
      {(target || hint) && (
        <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
          {target && <>{t('ak_bud2_target', { t: target })}</>}{target && hint ? ' · ' : ''}{hint}
        </div>
      )}
    </div>
  );
}

function Panel({ Icon, title, hint, children }: {
  Icon: LucideIcon; title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="tile p-4">
      <div className="flex items-start justify-between gap-3 pb-3 mb-1"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }}>{title}</span>
        </div>
        {hint && <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function AccuracySection({ data }: { data: AccuracyResp }) {
  const { t } = useTranslation();
  const g = data.generation, p = data.price, tg = data.targets.generation, tp = data.targets.price;
  const r = data.revenue, z = data.priceZones;
  const sgn = (v: number) => (v > 0 ? '+' : '');

  if (!g.days && !p.days) {
    return (
      <div className="tile p-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
        {t('ak_bud2_there_are_no_forecast_actual_pairs_for')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel Icon={Sun} title={t('ak_bud2_generation_forecast_accuracy')}
          hint={t('ak_bud2_horizon_days', { h: g.horizon, d: g.days })}>
          <Metric label={t('ak_bud2_hit_rate_10_tolerance')} value={pct(g.hitRate, 0)}
            target={pct(tg.hitRate, 0)} ok={g.hitRate != null ? g.hitRate >= tg.hitRate : null} />
          <Metric label="MAPE" value={pct(g.mape)} target={`≤ ${pct(tg.mape, 0)}`}
            ok={g.mape != null ? g.mape <= tg.mape : null} />
          <Metric label={t('ak_bud2_bias')} value={g.bias == null ? '—' : `${sgn(g.bias)}${pct(g.bias)}`}
            target={`±${pct(tg.bias, 0)}`}
            ok={g.bias != null ? Math.abs(g.bias) <= tg.bias : null}
            hint={t('ak_bud2_systematic_over_under_estimation')} />
          {/* Погодинні метрики проти номінації MMS не описують якість прогнозу —
              віддаємо їх окремо й з причиною, а не як провал моделі. */}
          <div className="mt-3 pt-3 text-[11px]" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
            {t('ak_bud2_profile_correlation_and_nmae_are_calcu', { c: n2(g.corrProfile), n: pct(g.nmae), note: g.profileNote })}
          </div>
        </Panel>

        <Panel Icon={Coins} title={t('ak_bud2_price_forecast_accuracy')}
          hint={t('ak_bud2_horizon_days', { h: p.horizon, d: p.days })}>
          <Metric label={t('ak_bud2_hit_rate_15_tolerance')} value={pct(p.hitRate, 0)}
            target={pct(tp.hitRate, 0)} ok={p.hitRate != null ? p.hitRate >= tp.hitRate : null} />
          <Metric label="MAPE" value={pct(p.mape)} target={`≤ ${pct(tp.mape, 0)}`}
            ok={p.mape != null ? p.mape <= tp.mape : null} />
          <Metric label={t('ak_bud2_nmae_vs_base')} value={pct(p.nmae)} target={`≤ ${pct(tp.nmae, 0)}`}
            ok={p.nmae != null ? p.nmae <= tp.nmae : null}
            hint={p.nmaeBase ? t('ak_bud2_base_uah_mwh', { v: n0(p.nmaeBase) }) : undefined} />
          <Metric label={t('ak_bud2_bias')} value={p.bias == null ? '—' : `${sgn(p.bias)}${pct(p.bias)}`}
            target={`±${pct(tp.bias, 0)}`} ok={p.bias != null ? Math.abs(p.bias) <= tp.bias : null} />
          <Metric label={t('ak_bud2_profile_correlation')} value={n2(p.corrProfile)}
            target={`≥ ${n2(tp.corrProfile)}`}
            ok={p.corrProfile != null ? p.corrProfile >= tp.corrProfile : null} />
          {(z.dip || z.peak) && (
            <div className="mt-3 pt-3 text-[11px]" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
              {z.dip && <>{t('ak_bud2_dip_11_16_forecast_actual', { p: n0(z.dip.planAvg), f: n0(z.dip.factAvg), e: sgn(z.dip.pctErr ?? 0) + pct(z.dip.pctErr, 0) })}</>}
              {z.dip && z.peak && <> · </>}
              {z.peak && <>{t('ak_bud2_peak_18_23_forecast_actual', { p: n0(z.peak.planAvg), f: n0(z.peak.factAvg), e: sgn(z.peak.pctErr ?? 0) + pct(z.peak.pctErr, 0) })}</>}
            </div>
          )}
        </Panel>
      </div>

      {r && (
        <Panel Icon={Split} title={t('ak_bud2_revenue_difference_breakdown')}
          hint={t('ak_bud2_hours_volume_price_profile', { h: r.hours })}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6">
            <div>
              <Metric label={t('ak_bud2_revenue_per_plan')} value={t('ak_bud2_mn', { v: mln(r.planUah) })}
                hint={t('ak_bud2_mwh_uah_weighted', { a: n1(r.planMwh), b: n0(r.planWeightedPrice) })} />
              <Metric label={t('ak_bud2_revenue_per_actual')} value={t('ak_bud2_mn', { v: mln(r.factUah) })}
                hint={t('ak_bud2_mwh_uah_weighted', { a: n1(r.factMwh), b: n0(r.factWeightedPrice) })} />
              <Metric label={t('ak_bud2_difference')} value={t('ak_bud2_mn', { v: sgn(r.deltaUah) + mln(r.deltaUah) })}
                ok={r.deltaUah >= 0} />
            </div>
            <div>
              <Metric label={t('ak_bud2_volume_effect')} value={t('ak_bud2_mn', { v: sgn(r.volumeEffectUah) + mln(r.volumeEffectUah) })}
                hint={t('ak_bud2_actual_plan_weighted_plan_price')} />
              <Metric label={t('ak_bud2_profile_effect')} value={t('ak_bud2_mn', { v: sgn(r.profileEffectUah) + mln(r.profileEffectUah) })}
                hint={t('ak_bud2_shifting_export_to_other_hours_the_mon')} />
              <Metric label={t('ak_bud2_price_effect')} value={t('ak_bud2_mn', { v: sgn(r.priceEffectUah) + mln(r.priceEffectUah) })}
                hint={t('ak_bud2_market_deviation_from_the_forecast')} />
            </div>
            <div>
              {data.bess && (
                <>
                  <Metric label={t('ak_bud2_bess_charge_for_the_month')} value={t('ak_bud2_mwh', { v: n1(data.bess.chargeMwh) })} />
                  <Metric label={t('ak_bud2_bess_discharge_for_the_month')} value={t('ak_bud2_mwh', { v: n1(data.bess.dischargeMwh) })}
                    hint={t('ak_bud2_this_shift_is_what_produces_the_profil')} />
                </>
              )}
              <Metric label={t('ak_bud2_breakdown_residual')}
                value={t('ak_bud2_mn', { v: mln(r.residualUah, 3) })}
                ok={Math.abs(r.residualUah) < 1}
                hint={t('ak_bud2_check_sum_of_the_three_effects_revenue')} />
            </div>
          </div>
        </Panel>
      )}

      {data.meterBalance && data.meterBalance.deliveredMwh != null && (
        <Panel Icon={Gauge} title={t('ak_bud2_balance_by_askoe_meters')}
          hint={t('ak_bud2_days_channel_a_export', { d: data.meterBalance.days })}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6">
            <div>
              <Metric label={t('ak_bud2_pv_generation_ktp_meters', { n: data.meterBalance.pvMeters })}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.pvMwh) })} />
              <Metric label={t('ak_bud2_bess_discharge_meters', { n: data.meterBalance.bessMeters })}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.bessDischargeMwh) })} />
              <Metric label={t('ak_bud2_grid_export_commercial_110_kv')}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.deliveredMwh) })}
                hint={t('ak_bud2_this_is_the_revenue_basis')} />
            </div>
            <div>
              <Metric label={t('ak_bud2_backup_meter')}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.deliveredBackupMwh) })}
                ok={data.meterBalance.deliveredBackupMwh != null && data.meterBalance.deliveredMwh != null
                  && Math.abs(data.meterBalance.deliveredBackupMwh / data.meterBalance.deliveredMwh - 1) < 0.01}
                hint={t('ak_bud2_deviation_from_the_main_meter_should_b')} />
              <Metric label={t('ak_bud2_verification_10_kv')}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.verificationMwh) })} />
              <Metric label={t('ak_bud2_balance_residual')}
                value={t('ak_bud2_mwh', { v: n1(data.meterBalance.residualMwh) })}
                hint={t('ak_bud2_bess_charge_auxiliary_consumption_loss')} />
            </div>
            <div>
              {/* Канал A+ (надходження) у вивантаженні АСКОЕ відсутній, тож
                  чистий заряд не виміряний — звідси ВЕРХНЯ межа коефіцієнта:
                  справжній нижчий, бо в залишку є ще власні потреби й втрати. */}
              <Metric label={t('ak_bud2_charge_coefficient_upper_limit')}
                value={n2(data.meterBalance.chargeCoeffUpper)}
                ok={data.meterBalance.chargeCoeffUpper != null
                  && data.meterBalance.chargeCoeffUpper < data.meterBalance.dogmaChargeCoeff}
                hint={t('ak_bud2_is_assumed_in_the_calculations', { v: n2(data.meterBalance.dogmaChargeCoeff) })} />
              <div className="text-[10.5px] mt-2 pt-2"
                style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
                {t('ak_bud2_channel_a_import_is_absent_from_the_ex')}
              </div>
            </div>
          </div>
        </Panel>
      )}

      {data.topErrors.length > 0 && (
        <Panel Icon={Target} title={t('ak_bud2_days_with_the_largest_generation_forec')}
          hint={t('ak_bud2_top_5_by_absolute_deviation')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6">
            {data.topErrors.map((t) => (
              <Metric key={t.day} label={`${dd(t.day)} ${t.day.slice(5, 7)}`}
                value={`${sgn(t.pctErr)}${pct(t.pctErr, 0)}`}
                ok={Math.abs(t.pctErr) <= 0.10}
                hint={i18n.t('ak_bud2_plan_actual_mwh', { a: n1((t.plan ?? 0) / 1000), f: n1((t.fact ?? 0) / 1000) })} />
            ))}
          </div>
        </Panel>
      )}

      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px]"
        style={{ color: 'var(--text-dim)' }}>
        <span className="inline-flex items-center gap-1">
          <Gauge size={12} strokeWidth={2} />
          {t('ak_bud2_definition_of_actuals', { d: data.factDefinition })}
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span className="inline-flex items-center gap-1">
          <TrendingUp size={12} strokeWidth={2} />
          {t('ak_bud2_first_three_months_observation_then_th')}
        </span>
      </div>
    </div>
  );
}
